/**
 * Pestaña Rastreo: la consola de seguimiento, con la forma de Traccar y la
 * estructura de WOX, en colores de GuardIA (especificación `specs/rastreo-registro/`).
 *
 *   ┌──────────────── barra de Drive ────────────────┐
 *   │ buscar · filtro · +   │                         │
 *   │ ▸ Cliente A (3)       │          mapa           │
 *   │   ○ ABC123  En línea  │                         │
 *   │ ▸ Sin cliente (1)     ├─────────────────────────┤
 *   │ Lista·Mapa·Salir      │ Info·Sensores·Informe.. │
 *   └───────────────────────┴─────────────────────────┘
 *
 * Abajo del mapa, fijo, va el panel del equipo elegido con sus pestañas (RR-28). El mapa
 * es un solo elemento: nada de lo que se haga en el panel lo vuelve a crear.
 *
 * En celular se ve un panel a la vez —lista o mapa— y la navegación
 * queda abajo (RR-12).
 *
 * Dos fuentes, cada una por su lado: el motor (`/api/rastreo/fleet`, refrescado
 * cada 10 s con el mapa a la vista y cada 15 s sin él) y el registro manual
 * (`/api/rastreo/registry`). Si el motor no responde, el registro se sigue viendo
 * (RR-14). La unión la hace el núcleo.
 *
 * Sin equipo elegido el mapa muestra solo los puntos de la flota; el recorrido es
 * del equipo elegido, y cerrar su panel lo suelta (RR-22).
 *
 * Recorrido en vivo (RR-18): si el período incluye el momento actual, la cola del
 * recorrido del equipo elegido se pide cada 5 s y el marcador sigue al punto más
 * nuevo. El mapa no se reencuadra con cada punto; «Seguir» lo centra sin tocar el zoom.
 *
 * Solo lectura sobre el equipo: ningún control le manda algo al GPS (Art. 6).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RASTREO_MAX_PERIOD_DAYS, type RastreoPosition, type RastreoTrackResponse } from "@core/contracts/rastreo.ts";
import type {
  CrearEquipoForm,
  EditarEquipoForm,
  RastreoRegistryResponse,
  SaveEquipoResponse,
} from "@core/contracts/rastreo-registro.ts";
import { filterRows, groupRows, mergeRastreo, type RastreoRow, type RowFilter } from "@core/rastreo/registro.ts";
import {
  appendLive,
  combineTrack,
  isLivePeriod,
  latestValidPoint,
  LIVE_FULL_REFRESH_MS,
  LIVE_LOOKBACK_MS,
  LIVE_REFRESH_MS,
  liveWindow,
  tailStart,
  withLivePosition,
} from "@core/rastreo/live-track.ts";
import {
  ApiError,
  createRastreoEquipo,
  fetchRastreoEvents,
  fetchRastreoFleet,
  fetchRastreoMapConfig,
  fetchRastreoPendientes,
  fetchRastreoRegistry,
  fetchRastreoTrack,
  updateRastreoEquipo,
} from "@web/lib/api-client.ts";
import { VehicleMap } from "@web/components/vehicle-map.tsx";
import type { MapTrackPoint, MapVehicle } from "@web/lib/maps/map-adapter.ts";
import { DeviceList } from "./device-list.tsx";
import { useAlertRules } from "./alert-rules.ts";
import { VehicleDock, type DockTab } from "./vehicle-dock.tsx";
import { DeviceForm, type DeviceFormTarget } from "./device-form.tsx";
import type { PeriodControls } from "./rastreo-reports.tsx";
import { ago } from "./rastreo-format.ts";
import { ListIcon, LogoutIcon, MapIcon } from "./rastreo-icons.tsx";

/**
 * Cada cuánto se refresca la flota mientras la pestaña está visible (RR-19). Con el mapa a la
 * vista, cada 10 s: sin equipo elegido, los puntos de la flota son lo único que se mueve en el
 * mapa (RR-22, pedido de Fabián del 30/10/2026). Con el mapa tapado, la lista en celular, 15 s.
 */
const FLEET_MAP_REFRESH_MS = 10_000;
const FLEET_REFRESH_MS = 15_000;
/** Recorrido y alarmas cuando no hay vivo: período pasado o lista en celular. */
const REFRESH_MS = 30_000;

/**
 * Lo que llegó en vivo del equipo elegido, aparte del recorrido base. `desde`: desde cuándo cubre
 * la cola; si empezó después del último punto del base, el mapa no une el hueco.
 */
type LiveTail = { points: RastreoPosition[]; added: number; checkedAt: number | null; desde: number | null };
const EMPTY_LIVE: LiveTail = { points: [], added: 0, checkedAt: null, desde: null };

type View = "lista" | "mapa";

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : "Error inesperado.");

export function RastreoPanel({
  period,
  dates,
  onSignOut,
  signingOut = false,
  now = () => Date.now(),
}: {
  period: { fromIso: string; toIso: string } | null;
  /** El mismo período de las otras pestañas: se edita aquí, en las pestañas con período. */
  dates: Omit<PeriodControls, "tooLong">;
  onSignOut: () => void;
  signingOut?: boolean;
  /** Para pruebas: reloj fijo. */
  now?: () => number;
}) {
  const queryClient = useQueryClient();
  // Cada lectura le pasa a `fetch` la señal de react-query: lo que deja de hacer falta se corta.
  const mapConfig = useQuery({
    queryKey: ["rastreo", "config"],
    queryFn: ({ signal }) => fetchRastreoMapConfig(signal),
    staleTime: Infinity,
  });
  const [view, setView] = useState<View>("lista");
  // El mapa se ve en «Mapa» y, en escritorio, también en «Lista». En celular, «Lista» lo tapa.
  const wide = useMinWidth(1024);
  const mapVisible = view === "mapa" || (view === "lista" && wide);
  const fleet = useQuery({
    queryKey: ["rastreo", "fleet"],
    queryFn: ({ signal }) => fetchRastreoFleet(signal),
    // react-query no refresca con la pestaña del navegador en segundo plano.
    refetchInterval: mapVisible ? FLEET_MAP_REFRESH_MS : FLEET_REFRESH_MS,
  });
  const registry = useQuery({ queryKey: ["rastreo", "registry"], queryFn: ({ signal }) => fetchRastreoRegistry(signal) });
  // Los GPS que transmiten sin registro (RR-23) están en la lista: se releen mientras se ve.
  const listVisible = view === "lista" || wide;
  const pendientes = useQuery({
    queryKey: ["rastreo", "pendientes"],
    queryFn: ({ signal }) => fetchRastreoPendientes(signal),
    refetchInterval: listVisible ? REFRESH_MS : false,
  });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // El panel inferior sigue al equipo elegido: abierto o contraído, y la pestaña que se estaba viendo (RR-28).
  const [dockOpen, setDockOpen] = useState(false);
  const [dockTab, setDockTab] = useState<DockTab>("informacion");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<RowFilter>("todos");
  const [form, setForm] = useState<DeviceFormTarget | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [following, setFollowing] = useState(false);

  const rows = useMemo(
    () =>
      mergeRastreo(
        registry.data?.equipos ?? [],
        registry.data?.clientes ?? [],
        fleet.data ? fleet.data.vehicles : null,
        registry.data?.relaciones ?? [],
      ),
    [registry.data, fleet.data],
  );
  const deviceId = rows.find((row) => row.key === selectedKey)?.vehicle?.deviceId ?? null;

  const tooLong =
    period !== null && Date.parse(period.toIso) - Date.parse(period.fromIso) > RASTREO_MAX_PERIOD_DAYS * 86_400_000;
  const trackPeriod = period && !tooLong ? period : null;
  const clock = now();
  const health = fleet.data?.health ?? null;

  const trackKey = ["rastreo", "track", deviceId, trackPeriod?.fromIso, trackPeriod?.toIso] as const;
  const liveKey = ["rastreo", "live", deviceId, trackPeriod?.fromIso, trackPeriod?.toIso] as const;
  // Vivo: hay equipo elegido, el período incluye el momento actual y el mapa está a la vista.
  const liveWanted = Boolean(deviceId) && mapVisible && isLivePeriod(trackPeriod, clock);

  const track = useQuery({
    queryKey: trackKey,
    queryFn: ({ signal }) => fetchRastreoTrack(deviceId!, trackPeriod!, signal),
    enabled: Boolean(deviceId && trackPeriod),
    // Con el vivo encendido, lo nuevo llega por la cola: el período entero se relee poco. Con el
    // mapa tapado (la lista en celular) no se relee: nadie lo está mirando.
    refetchInterval: liveWanted ? LIVE_FULL_REFRESH_MS : mapVisible ? REFRESH_MS : false,
  });
  const liveOn = liveWanted && track.isSuccess;
  const live = useQuery({
    queryKey: liveKey,
    // Pide solo la cola desde el último punto conocido (máximo 10 minutos atrás) y la
    // une a lo que ya había, sin repetidos.
    queryFn: async ({ signal }): Promise<LiveTail> => {
      const base = queryClient.getQueryData<RastreoTrackResponse>(trackKey)?.points ?? [];
      const anterior = queryClient.getQueryData<LiveTail>(liveKey) ?? EMPTY_LIVE;
      const previous = anterior.points;
      const window = liveWindow({ period: trackPeriod, base, live: previous, nowMs: now() });
      if (!deviceId || !window) return { ...anterior, added: 0, checkedAt: now() };
      // Si se corta, no se une nada: la cola queda como estaba.
      const page = await fetchRastreoTrack(deviceId, window, signal);
      const next = appendLive(base, previous, page.points);
      return {
        points: next.live,
        added: next.added,
        checkedAt: now(),
        desde: tailStart(anterior.desde, previous.length, next.live.length, window.fromIso),
      };
    },
    enabled: liveOn,
    // La primera consulta en vivo va 5 s después del recorrido completo, no encima de él.
    initialData: EMPTY_LIVE,
    staleTime: LIVE_REFRESH_MS,
    refetchInterval: LIVE_REFRESH_MS,
    // Si falla, el siguiente intento ya está a 5 s: no se apilan reintentos.
    retry: false,
  });

  // Al retomar el vivo después de más de 10 minutos (volver de la lista en celular o a la pestaña),
  // la cola no alcanza a cubrir lo que pasó: se relee el recorrido completo, y mientras tanto el
  // hueco no se une con una recta (`desde` en la cola; tercera revisión independiente).
  const trackKeyRef = useRef(trackKey);
  trackKeyRef.current = trackKey;
  const refreshStaleBase = useCallback(() => {
    const state = queryClient.getQueryState(trackKeyRef.current);
    if (state?.dataUpdatedAt && Date.now() - state.dataUpdatedAt > LIVE_LOOKBACK_MS) {
      void queryClient.invalidateQueries({ queryKey: trackKeyRef.current, exact: true });
    }
  }, [queryClient]);
  const wasLive = useRef(liveWanted);
  useEffect(() => {
    if (liveWanted && !wasLive.current) refreshStaleBase();
    wasLive.current = liveWanted;
  }, [liveWanted, refreshStaleBase]);

  // Cancelación de lo que quedó en vuelo:
  // - al cambiar de equipo o de período, react-query corta solo la consulta vieja (su clave
  //   cambió y nadie la mira);
  // - al apagarse el vivo (la lista en celular, período pasado) la consulta sigue
  //   observada, así que se corta aquí;
  // - con la pestaña oculta no se sondea, y todo lo de Rastreo que estaba en vuelo se corta.
  //   Al volver se pide ya lo que se cortó antes de tener datos: en producción
  //   `refetchOnWindowFocus` está apagado y, si no, el recorrido esperaría hasta 5 minutos.
  useEffect(() => {
    if (!liveOn) void queryClient.cancelQueries({ queryKey: ["rastreo", "live"] });
  }, [liveOn, queryClient]);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        void queryClient.cancelQueries({ queryKey: ["rastreo"] });
        return;
      }
      refreshStaleBase();
      void queryClient.refetchQueries({
        queryKey: ["rastreo"],
        type: "active",
        // Sin datos, o marcada para releer (por ejemplo, el registro después de guardar).
        predicate: (query) => query.state.data === undefined || query.state.isInvalidated,
      });
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [queryClient, refreshStaleBase]);

  const combined = useMemo(
    () => combineTrack(track.data ?? null, live.data.points, live.data.desde),
    [track.data, live.data.points, live.data.desde],
  );
  const latest = useMemo(() => latestValidPoint(combined), [combined]);
  // El equipo elegido muestra la posición en vivo si es más nueva que la de la flota.
  // La frescura se mide en el momento de la consulta en vivo; sin consulta aún, manda el motor.
  const freshnessS = live.data.checkedAt === null ? null : (health?.thresholds.freshnessS ?? null);
  const liveRows = useMemo(
    () =>
      latest
        ? rows.map((row) =>
            row.key === selectedKey && row.vehicle
              ? { ...row, vehicle: withLivePosition(row.vehicle, latest, live.data.checkedAt ?? 0, freshnessS) }
              : row,
          )
        : rows,
    [rows, latest, selectedKey, live.data.checkedAt, freshnessS],
  );

  const filtered = useMemo(() => filterRows(liveRows, query, filter), [liveRows, query, filter]);
  const groups = useMemo(() => groupRows(filtered), [filtered]);
  const selected = liveRows.find((row) => row.key === selectedKey) ?? null;

  // Las alarmas se piden una vez mientras haya un equipo elegido y el mapa se vea: de ellas depende si el
  // panel muestra «Alertas de dispositivo» y las columnas de alarma (un GPS sin alarmas no las muestra).
  // Se vuelven a pedir cada tanto solo con «Eventos de telemetría» o «Alertas» a la vista.
  // Al soltar al equipo, lo que estaba en vuelo se corta.
  const eventsWanted = mapVisible && Boolean(selected);
  const events = useQuery({
    queryKey: ["rastreo", "events", deviceId, trackPeriod?.fromIso, trackPeriod?.toIso],
    queryFn: ({ signal }) => fetchRastreoEvents(deviceId!, trackPeriod!, signal),
    enabled: Boolean(deviceId && trackPeriod && eventsWanted),
    refetchInterval: dockOpen && (dockTab === "telemetria" || dockTab === "alertas") ? REFRESH_MS : false,
  });
  useEffect(() => {
    if (!eventsWanted) void queryClient.cancelQueries({ queryKey: ["rastreo", "events"] });
  }, [eventsWanted, queryClient]);
  // Las reglas de alerta del equipo elegido (aviso de cada alerta y las agregadas a mano). Se leen
  // con el equipo elegido, igual que las alarmas; escribirlas no le manda nada al GPS (Art. 6).
  const alertRules = useAlertRules(deviceId, eventsWanted);

  const mapVehicles = useMemo<MapVehicle[]>(
    () =>
      liveRows.flatMap((row) =>
        row.vehicle?.lastPosition
          ? [
              {
                id: row.key,
                label: row.name,
                latitude: row.vehicle.lastPosition.latitude,
                longitude: row.vehicle.lastPosition.longitude,
                freshness: row.vehicle.telemetryFreshness,
              },
            ]
          : [],
      ),
    [liveRows],
  );
  const mapTrack = useMemo<MapTrackPoint[]>(
    () =>
      combined.map(({ position, breakBefore }) =>
        breakBefore
          ? { latitude: position.latitude, longitude: position.longitude, breakBefore: true }
          : { latitude: position.latitude, longitude: position.longitude },
      ),
    [combined],
  );
  const follow = following && liveOn && latest ? { latitude: latest.latitude, longitude: latest.longitude } : null;
  // Va en el panel inferior, junto al nombre del equipo: el mapa no lleva nada encima.
  const liveBadge = (
    <LiveBadge
      points={combined.length}
      truncated={Boolean(track.data?.truncated)}
      lastAt={latest ? (latest.deviceTime ?? latest.serverReceivedAt) : null}
      failing={live.isError}
      following={following}
      onFollow={() => setFollowing((current) => !current)}
      now={clock}
    />
  );
  // Sin vivo (un período que ya pasó), el recorrido dice cuántos puntos trae.
  const trackSummary =
    track.data && track.data.points.length > 0
      ? `Recorrido del período: ${track.data.points.length} punto(s)${track.data.truncated ? " (recortado)" : ""}`
      : null;
  // Soltar el equipo (RR-22): el recorrido desaparece, la cola en vivo y el recorrido dejan de
  // pedirse —lo que estaba en vuelo se corta, porque su consulta se queda sin quien la mire— y el
  // mapa vuelve a mostrar solo la flota.
  const release = useCallback(() => {
    setSelectedKey(null);
    setFollowing(false);
    // Sin equipo, el panel queda como barra: no hay nada que mostrar en su cuerpo.
    setDockOpen(false);
  }, []);

  const selectRow = useCallback(
    (row: RastreoRow) => {
      // El primer equipo que se elige abre el panel. Cambiar de equipo respeta si se contrajo.
      if (selectedKey === null) setDockOpen(true);
      setSelectedKey(row.key);
      // En celular, elegir en la lista lleva al mapa.
      setView("mapa");
    },
    [selectedKey],
  );
  const selectKey = useCallback(
    (key: string) => {
      const row = rows.find((item) => item.key === key);
      if (row) selectRow(row);
      else release();
    },
    [rows, selectRow, release],
  );

  // Si el equipo elegido desaparece de las dos fuentes, se suelta la selección. Mientras
  // alguna se está releyendo no: justo después de guardar, el registro nuevo todavía no llegó.
  const refreshing = registry.isFetching || fleet.isFetching;
  useEffect(() => {
    if (refreshing || !selectedKey || rows.some((row) => row.key === selectedKey)) return;
    release();
  }, [rows, selectedKey, refreshing, release]);

  const save = (payload: CrearEquipoForm | EditarEquipoForm): Promise<SaveEquipoResponse> =>
    form?.mode === "editar"
      ? updateRastreoEquipo(form.equipo.id, payload as EditarEquipoForm)
      : createRastreoEquipo(payload as CrearEquipoForm);

  const onSaved = (result: SaveEquipoResponse) => {
    const wasEditing = form?.mode === "editar";
    setForm(null);
    setSelectedKey(`reg:${result.equipo.id}`);
    setDockOpen(true);
    setDockTab("informacion");
    const motorText = {
      registrado: " Quedó registrado en el motor de rastreo.",
      actualizado: " El motor de rastreo quedó actualizado.",
      sin_cambios: "",
    }[result.motor];
    setAnnouncement(
      `${result.equipo.nombre}: ${wasEditing ? "cambios guardados" : "dispositivo agregado"}${
        result.vinculoCambiado && wasEditing ? ", con vínculo nuevo de placa o cliente" : ""
      }.${motorText}`,
    );
    // El motor lo acaba de registrar: la flota se relee para que aparezca ya, sin esperar 30 s, y
    // deja de estar entre los que transmiten sin registro.
    if (result.motor === "registrado" || result.motor === "actualizado") {
      void queryClient.invalidateQueries({ queryKey: ["rastreo", "fleet"] });
      void queryClient.invalidateQueries({ queryKey: ["rastreo", "pendientes"] });
    }
    // El equipo guardado entra a la lista ya, sin esperar la relectura; la relectura trae
    // además el cliente si era nuevo.
    queryClient.setQueryData<RastreoRegistryResponse>(["rastreo", "registry"], (current) =>
      current
        ? { ...current, equipos: [...current.equipos.filter((item) => item.id !== result.equipo.id), result.equipo] }
        : current,
    );
    void queryClient.invalidateQueries({ queryKey: ["rastreo", "registry"] });
  };

  return (
    <div className="grid h-full min-h-0 w-full grid-cols-1 grid-rows-[minmax(0,1fr)_auto] bg-white lg:grid-cols-[380px_minmax(0,1fr)]">
      <aside
        aria-label="Lista de dispositivos"
        className={`${view === "lista" ? "flex" : "hidden"} min-h-0 flex-col border-gd-line bg-white lg:col-start-1 lg:row-start-1 lg:flex lg:border-r`}
      >
        <DeviceList
          groups={groups}
          total={rows.length}
          shown={filtered.length}
          query={query}
          onQuery={setQuery}
          filter={filter}
          onFilter={setFilter}
          selectedKey={selectedKey}
          onSelect={selectRow}
          onAdd={() => setForm({ mode: "crear" })}
          pendientes={pendientes.data?.items ?? []}
          onRegisterPendiente={(pendiente) => setForm({ mode: "crear", pendiente })}
          now={clock}
          notices={
            <SourceNotices
              registry={{ pending: registry.isPending, error: registry.isError ? errorText(registry.error) : null }}
              fleet={{
                pending: fleet.isPending,
                error: fleet.isError ? errorText(fleet.error) : null,
                incomplete: fleet.data ? !fleet.data.complete : false,
              }}
              announcement={announcement}
            />
          }
          footer={
            <p className="border-t border-gd-line bg-gd-mist px-4 py-2 text-[12px] text-gd-muted">
              {health
                ? `Motor: ${health.devices.online} de ${health.devices.active} en línea · última trama ${ago(health.frames.lastReceivedAt, clock)}.`
                : "Motor sin datos."}{" "}
              Sin comandos: desde aquí no se le envía nada al GPS.
            </p>
          }
        />
      </aside>

      <section
        aria-label="Mapa"
        className={`${view === "lista" ? "hidden" : "flex"} relative min-h-0 min-w-0 flex-col lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:flex`}
      >
        {/* El mapa es un solo elemento, montado mientras dure el panel: ni elegir un equipo, ni abrir o
            contraer el panel inferior, ni cambiar de pestaña lo vuelven a crear (cada carga de Google
            Maps se cobra). El panel está debajo, no encima: el mapa se achica, no queda tapado. */}
        <div className="relative min-h-0 w-full flex-1">
          <VehicleMap
            fill
            config={mapConfig.data}
            vehicles={mapVehicles}
            track={mapTrack}
            selectedId={selectedKey}
            onSelect={selectKey}
            fitKey={`${trackPeriod?.fromIso ?? ""}|${trackPeriod?.toIso ?? ""}`}
            follow={follow}
            unavailableHint="El panel de cada equipo sigue mostrando todos sus datos."
          />
        </div>
        <VehicleDock
            row={selected}
            now={clock}
            tab={dockTab}
            onTab={setDockTab}
            open={dockOpen}
            onToggleOpen={() => setDockOpen((current) => !current)}
            onClose={release}
            onEdit={() => selected?.equipo && setForm({ mode: "editar", equipo: selected.equipo })}
            onRegister={() => selected && setForm({ mode: "crear", motor: selected.vehicle })}
            dates={{ ...dates, tooLong }}
            track={track}
            events={events}
            alertRules={alertRules}
            health={health}
            live={
              liveOn ? (
                liveBadge
              ) : trackSummary ? (
                <p className="text-[13px] font-medium text-gd-navy">{trackSummary}</p>
              ) : null
            }
          />
      </section>

      <BottomNav
        view={view}
        onView={setView}
        onSignOut={onSignOut}
        signingOut={signingOut}
      />

      {form ? (
        <DeviceForm
          target={form}
          clientes={registry.data?.clientes ?? []}
          today={dates.today}
          onCancel={() => setForm(null)}
          onSaved={onSaved}
          save={save}
        />
      ) : null}
    </div>
  );
}

/**
 * Estado del vivo: cada cuánto se actualiza, cuándo llegó el último punto y el botón
 * «Seguir». Si la consulta falla se dice; el próximo intento va solo.
 */
function LiveBadge({
  points,
  truncated,
  lastAt,
  failing,
  following,
  onFollow,
  now,
}: {
  points: number;
  truncated: boolean;
  lastAt: string | null;
  failing: boolean;
  following: boolean;
  onFollow: () => void;
  now: number;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-gd-navy">
      <p role="status" className="flex items-center gap-1.5 font-medium">
        <span
          aria-hidden="true"
          className={`size-2.5 rounded-full ${failing ? "bg-amber-500" : "animate-pulse bg-emerald-500"}`}
        />
        {failing
          ? "En vivo en pausa: el motor no respondió, se reintenta en 5 s."
          : `En vivo · cada 5 s · último punto ${lastAt ? ago(lastAt, now) : "sin dato"}`}
      </p>
      <span className="text-gd-muted">
        {points} punto(s){truncated ? " · el período se recortó: falta un tramo" : ""}
      </span>
      <button
        type="button"
        aria-pressed={following}
        onClick={onFollow}
        className={`rounded-full border px-2.5 py-0.5 text-[12px] font-semibold transition-colors ${
          following ? "border-gd-blue bg-gd-blue text-white" : "border-gd-line-strong text-gd-blue hover:bg-gd-mist"
        }`}
      >
        {following ? <span aria-hidden="true">✓ </span> : null}
        Seguir
      </button>
    </div>
  );
}

/**
 * ¿La ventana tiene al menos ese ancho? Sin `matchMedia` (pruebas, navegadores viejos) se asume
 * que sí: es el caso de escritorio, el que muestra el mapa junto a la lista.
 */
function useMinWidth(px: number): boolean {
  const query = `(min-width: ${px}px)`;
  const read = () => (typeof window.matchMedia === "function" ? window.matchMedia(query).matches : true);
  const [matches, setMatches] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

/** De dónde salen los datos y qué falta, dicho arriba de la lista. */
function SourceNotices({
  registry,
  fleet,
  announcement,
}: {
  registry: { pending: boolean; error: string | null };
  fleet: { pending: boolean; error: string | null; incomplete: boolean };
  announcement: string;
}) {
  return (
    <div>
      <p
        role="status"
        aria-live="polite"
        className={announcement ? "border-b border-emerald-200 bg-emerald-50 px-4 py-2 text-[13px] text-emerald-900" : "sr-only"}
      >
        {announcement}
      </p>
      {registry.pending || fleet.pending ? (
        <p className="border-b border-gd-line bg-gd-mist px-4 py-2 text-[13px] text-gd-muted" role="status">
          {registry.pending ? "Leyendo el registro de equipos…" : "Consultando el motor de rastreo…"}
        </p>
      ) : null}
      {registry.error ? (
        <p className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-[13px] text-amber-950" role="alert">
          <strong className="font-semibold">No se pudo leer el registro.</strong> {registry.error} La lista muestra solo lo
          que reporta el motor.
        </p>
      ) : null}
      {fleet.error ? (
        <p className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-[13px] text-amber-950" role="alert">
          <strong className="font-semibold">El motor de rastreo no respondió.</strong> {fleet.error} La lista muestra lo
          registrado, sin posiciones.
        </p>
      ) : null}
      {fleet.incomplete ? (
        <p className="border-b border-gd-line bg-gd-mist px-4 py-2 text-[13px] text-gd-muted">
          La lista del motor se recortó: hay más equipos de los que muestra esta vista.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Navegación de abajo, como Traccar: Lista · Mapa · Cerrar sesión. En celular
 * muestra «Lista» solo ahí, porque la lista y el mapa no caben juntos.
 */
function BottomNav({
  view,
  onView,
  onSignOut,
  signingOut,
}: {
  view: View;
  onView: (view: View) => void;
  onSignOut: () => void;
  signingOut: boolean;
}) {
  const item = "flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 text-[12px] font-medium transition-colors";
  const tone = (active: boolean) => (active ? "text-gd-blue" : "text-gd-muted hover:text-gd-navy");
  return (
    <nav
      aria-label="Rastreo"
      className="flex border-t border-gd-line bg-white pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_16px_rgba(11,30,45,0.06)] lg:col-start-1 lg:row-start-2 lg:border-r"
    >
      <button
        type="button"
        onClick={() => onView("lista")}
        aria-current={view === "lista" ? "page" : undefined}
        className={`${item} ${tone(view === "lista")} lg:hidden`}
      >
        <ListIcon className="size-6" />
        Lista
      </button>
      <button
        type="button"
        onClick={() => onView("mapa")}
        aria-current={view === "mapa" ? "page" : undefined}
        className={`${item} ${tone(view === "mapa")} ${view === "lista" ? "lg:text-gd-blue" : ""}`}
      >
        <MapIcon className="size-6" />
        Mapa
      </button>
      <button type="button" onClick={onSignOut} disabled={signingOut} className={`${item} ${tone(false)} disabled:opacity-60`}>
        <LogoutIcon className="size-6" />
        {signingOut ? "Saliendo…" : "Cerrar sesión"}
      </button>
    </nav>
  );
}
